import {
  Grid,
  Typography,
  useTheme,
  Paper,
  Divider,
  TextField,
  Autocomplete,
  InputAdornment,
  Button,
  Snackbar,
  Alert,
  FormControlLabel,
  Checkbox,
} from '@mui/material'
import { MdPhoneEnabled } from 'react-icons/md'
import { FormDefaultState } from '../../utils/constants'
import { FormType, AlertType } from '../../utils/sharedTypes'
import { useEffect, useRef, useState } from 'react'
import { client } from '../../utils/client'
// import { exercices } from '../../utils/constants'
import { emailValidator, phoneNumberValidator } from '../../utils/validators'
import { useParams } from 'react-router-dom'
import { isStepValid } from '../../utils/misc'
import { ExternalInscriptionProductType, ProfessionType, SessionType } from '../../utils/sharedTypes'
import Bugsnag from '@bugsnag/js'
import { DateField } from '@mui/x-date-pickers'
import dayjs from 'dayjs'
import { DropZone, StartingDate } from '../index'
import { attestationBlockingReason, sameAttestationUrl, uniqueAttestationFileName } from '../../utils/attestationUrssaf'
import { waitForAttestationVerdict } from '../../utils/waitForAttestationVerdict'

type FIFPLInscriptionFormPropsType = {
  products: ExternalInscriptionProductType[]
  setClose: () => void
  refetch: boolean
  setRefetch: (args: boolean) => void
}

const FIFPLInscriptionForm = ({ products, setClose, refetch, setRefetch }: FIFPLInscriptionFormPropsType) => {
  const { provider_id } = useParams()
  const theme = useTheme()
  const [form, setForm] = useState<FormType>(FormDefaultState)
  const [analysingAttestation, setAnalysingAttestation] = useState(false)
  const submittingRef = useRef(false)
  const createInFlightRef = useRef(false)
  const attestationUrlRef = useRef(form.data.attestation_urssaf)
  const [alert, setAlert] = useState<AlertType>({
    severity: undefined,
    message: '',
  })

  useEffect(() => {
    setForm((prevState) => {
      return {
        ...prevState,
        data: {
          ...prevState.data,
          funding_organism: { code: 'FIFPL' },
        },
      }
    })
  }, [])

  useEffect(() => {
    //GET PROFESSIONS
    const productCode = form.data.product?.product_code || form.data.products[0]?.product_code
    if (productCode) {
      client
        .get(`/registration/content/professions/?product=${productCode.toLowerCase()}`)
        .then((resp) => {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          const data = resp.data.results as ProfessionType[]
          let reorderedData = data
          const indexOfOther = data?.findIndex((p) => p.code === 'unknown')
          if (indexOfOther !== -1) {
            reorderedData = data.slice(indexOfOther + 1)
            reorderedData.push(data[indexOfOther])
          }
          setForm((prevState) => {
            return {
              ...prevState,
              professions: reorderedData,
            }
          })
        })
        .catch((err: Error) => {
          Bugsnag.notify(err)
          console.error(err)
        })
    }
  }, [form.data.product, form.data.products])

  const handleChange = (key: string, value: string | boolean | Record<string, string>) => {
    setForm((prevState) => {
      const errors = prevState.errors
      delete errors[key]
      if (key === 'attestation_urssaf') {
        attestationUrlRef.current = value as string
        // Keep the submit lock while create POST is in flight to avoid duplicate registrations.
        if (!createInFlightRef.current) {
          submittingRef.current = false
        }
        setAnalysingAttestation(false)
      }

      switch (key) {
        case 'email':
          if (!emailValidator(value as string)) {
            errors['email'] = 'Veuillez renseigner un e-mail valide'
          }
          break
        case 'mobile_phone':
          if (!phoneNumberValidator(value as string)) {
            errors['mobile_phone'] = 'Veuillez renseigner un numéro de téléphone valide'
          }
          break
        case 'first_name':
          if ((value as string).length === 0) {
            errors['first_name'] = 'Veuillez renseigner un prénom'
          }
          break
        case 'last_name':
          if ((value as string).length === 0) {
            errors['last_name'] = 'Veuillez renseigner un nom de famille'
          }
          break
        default:
          break
      }

      return {
        ...prevState,
        data: {
          ...prevState.data,
          [key]: value,
          ...(key === 'attestation_urssaf' ? { attestation_urssaf_ai: null } : {}),
        },
        errors: errors,
      }
    })
  }

  const submit = () => {
    if (!provider_id) {
      setAlert({ severity: 'error', message: 'Provider inconnu' })
      return
    }
    if (submittingRef.current) return
    submittingRef.current = true
    const url = form.data.attestation_urssaf
    const release = () => {
      if (attestationUrlRef.current === url) submittingRef.current = false
    }
    const clearSubmitLock = () => {
      setAnalysingAttestation(false)
      submittingRef.current = false
    }
    const finish = (globalId: string) => {
      setAnalysingAttestation(true)
      waitForAttestationVerdict(globalId, url)
        .then((verdict) => {
          if (attestationUrlRef.current !== url) return
          const message = attestationBlockingReason(verdict)
          setForm((prevState) => {
            if (!sameAttestationUrl(prevState.data.attestation_urssaf, url)) return prevState
            const errors = { ...prevState.errors }
            if (message) errors.attestation_urssaf = message
            else delete errors.attestation_urssaf
            return {
              ...prevState,
              data: { ...prevState.data, global_id: globalId, attestation_urssaf_ai: verdict },
              errors,
            }
          })
          if (message) return
          setAlert({ severity: 'success', message: 'Inscription enregistrée' })
          setRefetch(!refetch)
          setClose()
        })
        .catch((err) => {
          setAlert({ severity: 'error', message: "Erreur lors de l'enregistrement" })
          console.error(err)
        })
        .finally(() => {
          if (attestationUrlRef.current !== url) return
          clearSubmitLock()
        })
    }
    const continueAfterSave = (globalId: string) => {
      if (attestationUrlRef.current !== url) {
        clearSubmitLock()
        return
      }
      finish(globalId)
    }
    if (form.data.global_id) {
      const { attestation_urssaf_ai, ...registrationFields } = form.data
      void attestation_urssaf_ai
      client
        .patch(`/registration/${form.data.global_id}/`, {
          ...registrationFields,
          attestation_urssaf: url,
        })
        .then(() => continueAfterSave(form.data.global_id))
        .catch((err) => {
          release()
          setAlert({ severity: 'error', message: "Erreur lors de l'enregistrement" })
          console.error(err)
        })
      return
    }
    createInFlightRef.current = true
    client
      .post(`registration/external/${provider_id}/`, form.data)
      .then((resp) => {
        createInFlightRef.current = false
        const globalId = (resp.data as { global_id?: string }).global_id
        if (!globalId) {
          submittingRef.current = false
          setAlert({ severity: 'success', message: 'Inscription enregistrée' })
          setRefetch(!refetch)
          setClose()
          return
        }
        setForm((prevState) => ({
          ...prevState,
          data: { ...prevState.data, global_id: globalId },
        }))
        continueAfterSave(globalId)
      })
      .catch((err) => {
        createInFlightRef.current = false
        // Always release: file replace during create skips clearSubmitLock in handleChange.
        submittingRef.current = false
        setAlert({ severity: 'error', message: "Erreur lors de l'enregistrement" })
        console.error(err)
      })
  }

  useEffect(() => {
    const productCode = form.data.product?.product_code || form.data.products[0]?.product_code
    if (
      productCode &&
      form.data.funding_organism?.code &&
      form.data.profession.code &&
      form.data.funding_organism.code === 'FIFPL'
    ) {
      client
        .get(
          `/registration/content/sessions/?product_code=${productCode.toLowerCase()}&funding_organism=${form.data.funding_organism.code.toLowerCase()}&profession=${form.data.profession.code.toLowerCase()}`,
        )
        .then((resp) => {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          const data = resp.data.results as SessionType[]
          setForm((prevState) => {
            return {
              ...prevState,
              data: {
                ...prevState.data,
                session:
                  prevState.data.session?.global_id || data.length === 0
                    ? prevState.data.session
                    : { global_id: data[0].global_id },
              },
              sessions: data,
            }
          })
        })
        .catch((err: Error) => {
          Bugsnag.notify(err)
          console.error(err)
        })
    }
  }, [form.data.product, form.data.products, form.data.funding_organism?.code, form.data.profession.code])

  return (
    <Grid container item xs={10} md={8} sx={{ maxHeight: '100%', overflow: 'scroll' }}>
      <Paper sx={{ background: 'white', p: theme.spacing(2), width: '100%', height: '100%' }}>
        <Typography variant="h6">Nouvelle inscription FIFPL</Typography>
        <Divider sx={{ my: 2 }} />
        <Grid item container spacing={2}>
          {form.data.product && (
            <Grid item xs={12}>
              <Autocomplete
                disableClearable
                disablePortal
                id="formation"
                value={form.data.product}
                getOptionLabel={(option) => `${option?.name} - ${option.product_code}`}
                onChange={(_, value) => {
                  if (value) {
                    handleChange('product', value as Record<string, any>)
                  }
                }}
                options={products}
                renderInput={(params) => <TextField {...params} required label="Formation" />}
              />
            </Grid>
          )}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Prénom"
              variant="outlined"
              id="first_name"
              required
              value={form.data.first_name}
              onChange={(e: React.FocusEvent<HTMLInputElement>) => {
                handleChange('first_name', e?.target.value)
              }}
              error={Boolean(form.errors.first_name)}
              helperText={form.errors.first_name}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Nom"
              id="last_name"
              variant="outlined"
              required
              value={form.data.last_name}
              onChange={(e: React.FocusEvent<HTMLInputElement>) => {
                handleChange('last_name', e?.target.value)
              }}
              error={Boolean(form.errors.last_name)}
              helperText={form.errors.last_name}
            />
          </Grid>
          <Grid item xs={12}>
            <Autocomplete
              disableClearable
              disablePortal
              id="profession"
              value={form.professions.filter((p) => p?.code === form.data.profession.code)[0] || null}
              getOptionLabel={(option) => option?.name}
              onChange={(_, value) => {
                if (value) {
                  handleChange('profession', { code: value.code })
                }
              }}
              options={form.professions}
              renderInput={(params) => <TextField {...params} required label="Profession" />}
            />
          </Grid>
          {/* <Grid item xs={12} md={6}>
            <Autocomplete
              disablePortal
              disableClearable
              value={exercices.filter((p) => p.code === form.data.exercice)[0] || null}
              id="exercice"
              onChange={(_, value) => {
                if (value) {
                  handleChange('exercice', value.code)
                }
              }}
              options={exercices}
              renderInput={(params) => <TextField {...params} required label="Mode d'exercice" />}
            />
          </Grid> */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              type="email"
              label="Adresse e-mail"
              id="email"
              variant="outlined"
              required
              value={form.data.email}
              onChange={(e: React.FocusEvent<HTMLInputElement>) => {
                handleChange('email', e?.target.value)
              }}
              error={Boolean(form.errors.email)}
              helperText={form.errors.email}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              type="tel"
              fullWidth
              id="mobile_phone"
              label="N° de mobile"
              variant="outlined"
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="start">
                    <MdPhoneEnabled size="20" />
                  </InputAdornment>
                ),
              }}
              value={form.data.mobile_phone}
              onChange={(e: React.FocusEvent<HTMLInputElement>) => {
                handleChange('mobile_phone', e?.target.value)
              }}
              error={Boolean(form.errors.mobile_phone)}
              helperText={form.errors.mobile_phone}
            />
          </Grid>
          <Grid item xs={12}>
            <DateField
              id="birthdate"
              label="Date de naissance"
              format="DD/MM/YYYY"
              disableFuture
              value={form.data.birthdate ? dayjs(form.data.birthdate) : null}
              onChange={(value) => {
                //HACKY
                if (value && dayjs(value).isValid() && `${dayjs(value).year()}`.length === 4) {
                  handleChange('birthdate', dayjs(value).format('YYYY-MM-DD'))
                }
              }}
              slots={{
                textField: (params) => (
                  <TextField
                    id="birthdate_textField"
                    {...params}
                    fullWidth
                    onBlur={() => {
                      if (!dayjs(form.data.birthdate).isValid()) {
                        setForm((prevState) => {
                          return {
                            ...prevState,
                            errors: {
                              ...prevState.errors,
                              birthdate: 'Veuillez entrer une date de naissance valide',
                            },
                          }
                        })
                      }
                    }}
                    error={Boolean(form.errors.birthdate)}
                    helperText={form.errors.birthdate}
                  />
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sx={{ width: '100%', mt: 1 }}>
            <DropZone
              message="Déposez ici l'attestation URSSAF"
              required
              maxFiles={1}
              maxSize={10}
              sx={{ minHeight: 100, background: theme.palette.background.default }}
              accept={{
                'image/*': ['.png', '.jpeg', '.jpg'],
                'application/pdf': ['.pdf'],
                'application/octet-stream': ['.pdf'],
              }}
              bucketName="walter-inscription"
              folderPath={form.data.global_id}
              fileName={(file) => uniqueAttestationFileName(file.name)}
              existingFileUrl={form.data.attestation_urssaf ?? undefined}
              onUploaded={(url: string) => {
                handleChange('attestation_urssaf', url)
              }}
            />
            {form.errors.attestation_urssaf ? (
              <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                {form.errors.attestation_urssaf}
              </Typography>
            ) : (
              analysingAttestation && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Analyse en cours
                </Typography>
              )
            )}
          </Grid>
          <Grid item xs={12}>
            <StartingDate form={form} setForm={setForm} fifplRules />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              labelPlacement="end"
              sx={{ alignItems: 'flex-start', flexWrap: 'nowrap' }}
              checked={form.data.budget}
              control={
                <Checkbox
                  id="budget"
                  sx={{
                    marginTop: -1.3,
                  }}
                />
              }
              onChange={(_, checked) => {
                handleChange('budget', checked)
              }}
              label={
                <Grid item sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography aria-haspopup="true" variant="body2">
                    Budget suffisant auprès du FIF-PL
                  </Typography>
                </Grid>
              }
            />
          </Grid>
        </Grid>
        <Grid item container spacing={2} sx={{ mt: 1, mb: 2 }}>
          <Grid item container xs={12}>
            <Button
              fullWidth
              disabled={analysingAttestation || !isStepValid('external_inscription_fifpl', form)}
              variant="contained"
              sx={{
                height: {
                  xs: 45,
                },
              }}
              onClick={() => {
                submit()
              }}
            >
              Enregistrer
            </Button>
          </Grid>
        </Grid>
      </Paper>
      <Snackbar
        open={Boolean(alert.message)}
        autoHideDuration={6000}
        onClose={() => {
          setAlert({ severity: undefined, message: '' })
        }}
      >
        <Alert
          onClose={() => {
            setAlert({ severity: undefined, message: '' })
          }}
          severity={alert.severity || 'info'}
          sx={{ width: '100%' }}
        >
          {alert.message}
        </Alert>
      </Snackbar>
    </Grid>
  )
}

export default FIFPLInscriptionForm
